import React, { useContext, useEffect, useState } from 'react';
import { Input, Button, Checkbox, Alert, Form } from 'antd';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { register } from '../services/user';
import SinglePageForm, {
    getEmailRules,
    getPasswordRules,
} from '../common/SinglePageForm';
import { APP_CCX, CustomAppContext } from '../common/CustomAppContext';
import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import './RegisterPage.less';
import {
    checkPasswordRequirements,
    trackCustomEvent,
} from '../helpers';
import classNames from 'classnames';

function isPersonalEmail(email) {
    if (!email || typeof email !== 'string') return false;

    const personalDomains = [
        'gmail.com',
        'yahoo.com',
        'hotmail.com',
        'outlook.com',
        'live.com',
        'aol.com',
        'msn.com',
        'icloud.com',
        'me.com',
        'protonmail.com',
        'yandex.com',
        'mail.com',
        'gmx.com',
        'zoho.com',
        'pm.me',
        'tutanota.com',
        'fastmail.com',
    ];

    const domain = email.trim().toLowerCase().split('@')[1];
    return personalDomains.includes(domain);
}

export default RegisterPage;

/**
 * @name Register
 * @desc Registration component.
 */
function RegisterPage(props) {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [passwordError, setPasswordError] = useState([]);
    const [promoCode, setPromoCode] = useState(null);
    const {
        currentApp,
        getCustomAppPath,
        getCustomAppButtonStyle,
        getCustomAppStartUrl,
    } = useContext(CustomAppContext);

    const location = useLocation();

    useEffect(() => {
        const urlParams = new URLSearchParams(location.search);
        const pcode = urlParams.get('pcode');
        if (pcode) {
            setPromoCode(pcode);
        }
    }, [location.search]);

    const validatePasswordRequirements = (password) => {
        const { isValid, errors } = checkPasswordRequirements(password);
        if (!isValid) {
            setPasswordError(errors);
            return false;
        }
        return true;
    };

    const handleSubmit = async (values) => {
        const { login, password, termsAccepted, allowNewsletters } = values;
        setLoading(true);
        try {
            await register({
                login,
                password,
                firstName: '',
                lastName: '',
                termsAccepted,
                allowNewsletters,
                origin: currentApp,
            });
            trackCustomEvent(
                currentApp,
                'user_registerated_successfully',
                'click'
            );
            window.location.assign(`${getCustomAppStartUrl({ promoCode })}`);
        } catch (err) {
            setError(err.message);
            trackCustomEvent(currentApp, 'user_registeration_failed', 'click');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmitFailed = (errorInfo) => {
        console.debug('Failed:', errorInfo);
        setError('Please check your input and try again.');
    };

    // Get email rules with promo code validation if needed
    const getEmailRulesWithPromo = () => {
        const rules: any[] = getEmailRules();
        if (promoCode) {
            rules.push({
                validator: (_, value) => {
                    if (isPersonalEmail(value)) {
                        return Promise.reject(
                            new Error(
                                'A business email is required to register with this promo code.'
                            )
                        );
                    }
                    return Promise.resolve();
                },
            });
        }
        return rules;
    };

    return (
        <SinglePageForm
            form={form}
            data-testid="register-form"
            onFinish={handleSubmit}
            onFinishFailed={handleSubmitFailed}
        >
            <SinglePageForm.Content>
                <h1 className="RegisterPage-form-title">Sign up </h1>

                <Form.Item>
                    Already have an account?{' '}
                    <NavLink
                        to={getCustomAppPath('/')}
                        className={`RegisterPage-link-${currentApp}`}
                    >
                        Log In
                    </NavLink>
                </Form.Item>

                <Form.Item
                    label="Email"
                    name="login"
                    rules={getEmailRulesWithPromo()}
                    validateTrigger="onBlur"
                >
                    <Input
                        data-testid="register-form-input-email"
                        disabled={loading}
                        placeholder="Enter your email"
                    />
                </Form.Item>

                <Form.Item
                    label="Password"
                    name="password"
                    rules={getPasswordRules(true)}
                    validateTrigger="onBlur"
                >
                    <Input.Password
                        data-testid="register-form-input-password"
                        disabled={loading}
                        onChange={(e) => {
                            validatePasswordRequirements(e.target.value);
                        }}
                        placeholder="Enter password of your choice"
                    />
                </Form.Item>

                <Form.Item noStyle shouldUpdate={true}>
                    {({ getFieldError }) =>
                        getFieldError('password')?.length > 0 ? (
                            <Form.Item>
                                <div
                                    className={`RegisterPage-validation-${currentApp}`}
                                >
                                    <p className="RegisterPage-validation-heading">
                                        A valid password must contain at least:
                                    </p>
                                    <div
                                        className={
                                            'RegisterPage-validation-box'
                                        }
                                    >
                                        {passwordError.map((check, index) => (
                                            <span
                                                className={classNames(
                                                    'RegisterPage-validation-points',
                                                    {
                                                        success: check.valid,
                                                        error: !check.valid,
                                                    }
                                                )}
                                                key={index}
                                            >
                                                {check.valid ? (
                                                    <CheckCircleOutlined
                                                        onPointerEnterCapture={
                                                            undefined
                                                        }
                                                        onPointerLeaveCapture={
                                                            undefined
                                                        }
                                                    />
                                                ) : (
                                                    <CloseCircleOutlined
                                                        onPointerEnterCapture={
                                                            undefined
                                                        }
                                                        onPointerLeaveCapture={
                                                            undefined
                                                        }
                                                    />
                                                )}{' '}
                                                {check.message}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </Form.Item>
                        ) : null
                    }
                </Form.Item>

                <Form.Item
                    name="allowNewsletters"
                    valuePropName="checked"
                    initialValue={false}
                >
                    <Checkbox
                        data-testid="register-form-input-send-communications"
                        className={`RegisterPage-checkbox-${currentApp}`}
                    >
                        I agree to receive marketing messages
                    </Checkbox>
                </Form.Item>

                <Form.Item
                    name="termsAccepted"
                    valuePropName="checked"
                    initialValue={false}
                    rules={[
                        {
                            required: true,
                            transform: (value) => value || undefined,
                            type: 'boolean',
                            message: 'Please accept Terms and Conditions.',
                        },
                    ]}
                >
                    <Checkbox
                        data-testid="register-form-input-terms-and-conditions"
                        className={`RegisterPage-checkbox-${currentApp}`}
                    >
                        I have read and agree to the{' '}
                        <Link
                            to={getCustomAppPath('/terms-and-conditions')}
                            target="_blank"
                            className={`RegisterPage-link-${currentApp}`}
                        >
                            {currentApp.toUpperCase()} Terms of service
                        </Link>{' '}
                        and{' '}
                        <Link
                            to={getCustomAppPath('/privacy-policy')}
                            target="_blank"
                            className={`RegisterPage-link-${currentApp}`}
                        >
                            Privacy Policy
                        </Link>
                    </Checkbox>
                </Form.Item>

                <SinglePageForm.Footer>
                    {error ? (
                        <Alert message={error} type="error" showIcon={true} />
                    ) : null}
                    {promoCode && (
                        <Alert
                            message={
                                <div style={{ textAlign: 'left' }}>
                                    Promo code <strong>{promoCode}</strong> is
                                    ready to be used.
                                </div>
                            }
                            type="info"
                            showIcon
                        />
                    )}
                    <br />
                    <Button
                        data-testid="register-form-button-submit"
                        loading={loading}
                        type="primary"
                        htmlType="submit"
                        style={getCustomAppButtonStyle()}
                        className={`RegisterPage-form-button-${currentApp}`}
                    >
                        Register
                    </Button>
                </SinglePageForm.Footer>
            </SinglePageForm.Content>

            {/* https://dev.to/felipperegazio/how-to-create-a-simple-honeypot-to-protect-your-web-forms-from-spammers--25n8 */}
            <label className="ohnohoney" htmlFor="firstName" />
            <input
                className="ohnohoney"
                autoComplete="off"
                type="text"
                id="firstName"
                name="firstName"
                placeholder="Enter your first name here"
            />
            <label className="ohnohoney" htmlFor="lastName" />
            <input
                className="ohnohoney"
                autoComplete="off"
                type="text"
                id="lastName"
                name="lastName"
                placeholder="Enter your first name here"
            />
            <label className="ohnohoney" htmlFor="email" />
            <input
                className="ohnohoney"
                autoComplete="off"
                type="email"
                id="email"
                name="email"
                placeholder="Enter your e-mail here"
            />
            <label className="ohnohoney" htmlFor="confirmEmail" />
            <input
                className="ohnohoney"
                autoComplete="off"
                type="email"
                id="confirmEmail"
                name="confirmEmail"
                placeholder="Confirm your e-mail here"
            />
            <label className="ohnohoney" htmlFor="password" />
            <input
                className="ohnohoney"
                autoComplete="off"
                type="password"
                id="password"
                name="password"
                placeholder="Enter your password here"
            />
        </SinglePageForm>
    );
}
